import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from "@angular/core";
import {
  FormGroup,
  FormBuilder,
  Validators
} from "@angular/forms";
import { Subscription } from "rxjs";
import { FieldConfig } from "../../field.interface";
import { LimpiarService } from "../button/service/limpiar.service";
import { SubmitService } from "../submit/service/submit.service";

@Component({
  exportAs: "dynamicForm",
  selector: "dynamic-form",
  template: `
  <nb-card>
    <nb-card-body>
      <div class="row mb-2">
        <div class="col-12">
            <div [innerHtml]="header"></div>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <form class="dynamic-form" [formGroup]="form" (submit)="onSubmit($event)">
            <div class="row">
              <ng-container *ngFor="let field of fields;" dynamicField [field]="field" [callback]="field.callback" [group]="form" >
              </ng-container>
            </div>
          </form>
        </div>
      </div>
    </nb-card-body>
  </nb-card>
  `,
  styles: []
})
export class DynamicFormComponent implements OnInit {
  @Input() fields: FieldConfig[] = [];
  @Input() header: string = "";

  @Output() submit: EventEmitter<any> = new EventEmitter<any>();

  @Output() valueChanges: EventEmitter<any> = new EventEmitter<any>();

  private subscriptions: Subscription[] = [];

  form: FormGroup;

  get value() {
    return this.form.getRawValue();
  }

  get controls() {
    return this.fields.filter(({ type }) => type !== 'button');
  }

  get changes() {
    return this.form.valueChanges;
  }

  get valid() {
    return this.form.valid;
  }
  constructor(
    private fb: FormBuilder,
    private submitService: SubmitService,
    private limpiarService: LimpiarService) {
    this.limpiarService.limpiarForm.subscribe(v => {
      if (v) {
        if (this.form)
          this.form.reset();
      }
    });
  }

  ngOnInit() {
    this.form = this.createGroup();
    this.setValueChanges();
  }

  hasSubmitButton(): boolean {
    return this.fields.filter(f => f.type === "submit").length !== 0;
  }

  changeStateSubmit(state: boolean) {
    if (this.hasSubmitButton()) {
      this.submitService.submit.next(state);
    }
  }

  onSubmit(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.changeStateSubmit(true);
    if (this.form.valid) {
      this.submit.emit(this.form.getRawValue());
    } else {
      this.validateAllFormFields(this.form);
      this.changeStateSubmit(false);
    }
  }

  createGroup() {
    const group = this.fb.group({});
    this.fields.forEach(field => {
      if (field.type === "button" || field.type === "espacioEnBlanco" || field.type === "submit") return;
      const control = this.fb.control(
        field.value,
        this.bindValidations(field.validations || [])
      );
      group.addControl(field.name, control);
    });
    return group;
  }

  bindValidations(validations: any) {
    if (validations && validations.length > 0) {
      const validList = [];
      validations.forEach(valid => {
        validList.push(valid.validator);
      });
      return Validators.compose(validList);
    }
    return null;
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      control.markAsTouched({ onlySelf: true });
    });
  }

  setDisabled(name: string, disable: boolean) {
    if (this.form.controls[name]) {
      const method = disable ? 'disable' : 'enable';
      this.form.controls[name][method]();
      return;
    }

    this.fields = this.fields.map((item) => {
      if (item.name === name) {
        item.disabled = disable;
      }
      return item;
    });
  }

  setValue(name: string, value: any) {
    this.form.controls[name].setValue(value, { emitEvent: true });
  }

  createControl(config: FieldConfig) {
    const { disabled, validations, value } = config;
    return this.fb.control({ disabled, value }, this.bindValidations(validations));
  }

  addControl(field: FieldConfig) {
    this.form.addControl(field.name, this.fb.control(
      field.value,
      this.bindValidations(field.validations || [])
    ));
  }

  ngOnChanges() {
    if (this.form) {
      // saco todos los valuechanges porque si los saco y agrego se cuelven a ejecutar
      this.subscriptions.forEach((sub) => {
        sub.unsubscribe();
      })
      // ordeno para poner el button al final
      this.fields.push(this.fields.splice(this.fields.findIndex(f => f.type === "submit"), 1)[0]);

      const controls = Object.keys(this.form.controls);
      const configControls = this.controls.map((item) => item.name);

      controls
        .filter((control) => !configControls.includes(control))
        .forEach((control) => this.form.removeControl(control));

      configControls
        .filter((control) => !controls.includes(control))
        .forEach((name) => {
          const config = this.fields.find((control) => control.name === name);
          this.form.addControl(name, this.createControl(config));
        });

      this.setValueChanges();
    }
  }

  addFields(fields: FieldConfig[]) {
    fields.forEach(f => {
      this.addField(f);
    });
    this.ngOnChanges();
  }

  addField(field: FieldConfig) {
    this.fields.push(field);
  }

  removeFields(fields: FieldConfig[]) {
    fields.forEach(field => {
      this.removeField(field);
    });

    this.ngOnChanges();
  }

  removeField(field: FieldConfig) {
    var index = this.fields.findIndex(f => f.name === field.name);
    if (index !== -1) {
      this.fields.splice(index, 1);
    }
  }

  setValueChanges() {
    Object.keys(this.form.controls).forEach(field => {
      const control = this.form.get(field);
      this.subscriptions.push(control.valueChanges.subscribe(d => {
        this.valueChanges.emit({ name: field, value: d });
      }))
    });
  }

}
